import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Sparkles, Calendar, Users, CheckCircle, ArrowRight, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Navigation - Sticky */}
      <nav className={`fixed top-0 left-0 right-0 z-50 px-6 md:px-12 lg:px-20 py-5 transition-all duration-500 ${
        scrolled ? 'bg-[#FAF9F7]/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif tracking-tight text-[#2C2825]">VowFlow</span>
          </div>
          <Button 
            data-testid="nav-get-started-btn"
            onClick={() => navigate('/auth')} 
            className="px-6 py-3 bg-[#881337] hover:bg-[#6F0F2D] text-white text-sm font-medium transition-all duration-300"
          >
            Get Started
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-20 md:pt-32 md:pb-28 px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-6">
              <h1 
                data-testid="hero-heading"
                className="text-4xl sm:text-5xl md:text-6xl font-serif tracking-tight text-[#2C2825] leading-[1.1]"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                The Wedding Planning App That Shows You What to Do Next
              </h1>
              <p className="text-lg md:text-xl text-[#5C5652] leading-relaxed max-w-2xl">
                VowFlow helps engaged couples organize their wedding timeline, budget, and guest list in one place. 
                No more scattered spreadsheets or endless checklists—just clear guidance on what matters now.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                data-testid="hero-start-planning-btn"
                onClick={() => navigate('/auth')} 
                className="px-8 py-4 bg-[#881337] hover:bg-[#6F0F2D] text-white text-base font-medium transition-all duration-300 flex items-center gap-2"
              >
                Start Planning <ArrowRight size={18} />
              </Button>
              <Button 
                data-testid="hero-view-pricing-btn"
                variant="outline" 
                className="px-8 py-4 border-2 border-[#2C2825]/20 text-[#2C2825] hover:border-[#2C2825]/40 text-base font-medium transition-all duration-300"
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              >
                View Pricing
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1766734864456-12497ffcdce0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3ZWRkaW5nJTIwY291cGxlJTIwY2FuZGlkJTIwam95fGVufDB8fHx8MTc2Njg2MDM0OHww&ixlib=rb-4.1.0&q=85" 
                alt="Happy couple" 
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-sm p-6 shadow-lg">
                <p className="text-sm text-[#5C5652] mb-1">Join 10,000+ couples</p>
                <p className="text-xl font-serif text-[#881337]">Planning with Grace</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-4 text-center">What VowFlow Does</p>
          <h2 
            className="text-3xl md:text-4xl font-serif text-center mb-4 text-[#2C2825]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Everything You Need to Plan Your Wedding
          </h2>
          <p className="text-center text-[#5C5652] mb-16 max-w-2xl mx-auto">
            VowFlow connects your wedding timeline, budget, and guest list so you always know what to focus on next.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div data-testid="feature-card-timeline" className="bg-[#FAF9F7] p-8 border border-[#E8E5E1]">
              <div className="w-12 h-12 bg-[#881337]/10 flex items-center justify-center mb-6">
                <Calendar className="text-[#881337]" size={24} />
              </div>
              <h3 className="text-lg font-medium mb-3 text-[#2C2825]">Wedding Timeline</h3>
              <p className="text-[#5C5652] leading-relaxed text-sm">
                Get a personalized timeline that adapts to your wedding date. Tasks are sequenced in the right order, 
                so you focus on what matters now—not everything at once.
              </p>
            </div>
            
            <div data-testid="feature-card-budget" className="bg-[#FAF9F7] p-8 border border-[#E8E5E1]">
              <div className="w-12 h-12 bg-[#881337]/10 flex items-center justify-center mb-6">
                <TrendingUp className="text-[#881337]" size={24} />
              </div>
              <h3 className="text-lg font-medium mb-3 text-[#2C2825]">Wedding Budget</h3>
              <p className="text-[#5C5652] leading-relaxed text-sm">
                Track spending across every category—venue, catering, florals, photography, and more. 
                See where your money is going and make informed decisions.
              </p>
            </div>
            
            <div data-testid="feature-card-guests" className="bg-[#FAF9F7] p-8 border border-[#E8E5E1]">
              <div className="w-12 h-12 bg-[#881337]/10 flex items-center justify-center mb-6">
                <Users className="text-[#881337]" size={24} />
              </div>
              <h3 className="text-lg font-medium mb-3 text-[#2C2825]">Wedding Guest List</h3>
              <p className="text-[#5C5652] leading-relaxed text-sm">
                Organize your guests, track RSVPs, manage meal preferences, and keep contact details in one place. 
                No more scattered spreadsheets.
              </p>
            </div>
            
            <div data-testid="feature-card-assistant" className="bg-[#FAF9F7] p-8 border border-[#E8E5E1]">
              <div className="w-12 h-12 bg-[#881337]/10 flex items-center justify-center mb-6">
                <Sparkles className="text-[#881337]" size={24} />
              </div>
              <h3 className="text-lg font-medium mb-3 text-[#2C2825]">Know What Matters Next</h3>
              <p className="text-[#5C5652] leading-relaxed text-sm">
                VowFlow shows you exactly what to do next based on your wedding date and progress. 
                Stop wondering where to start—just follow the plan.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 md:py-28 px-6 md:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-4">Simple Pricing</p>
          <h2 
            className="text-3xl md:text-4xl font-serif tracking-tight text-[#2C2825]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Choose Your Plan
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div data-testid="pricing-basic-card" className="p-10 bg-white border border-[#E8E5E1] hover:shadow-lg transition-all duration-300">
            <div className="mb-8">
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-3">Essentials</p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-serif text-[#2C2825]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>$9.99</span>
                <span className="text-[#5C5652]">/month</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-[#5C5652]">Grace Assistant (limited)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-[#5C5652]">Guest list up to 100</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-[#5C5652]">Essential guest website</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-[#5C5652]">Timeline & checklist</span>
              </li>
            </ul>
            <Button 
              data-testid="pricing-basic-btn"
              onClick={() => navigate('/signup')} 
              className="w-full py-4 bg-[#FAF9F7] hover:bg-[#F0EDEA] text-[#2C2825] font-medium border border-[#E8E5E1] transition-all duration-300"
            >
              Get Started
            </Button>
          </div>

          <div data-testid="pricing-premium-card" className="p-10 bg-[#2C2825] relative">
            <div className="absolute top-4 right-4 bg-[#881337] text-white text-xs px-3 py-1 uppercase tracking-wide">Popular</div>
            <div className="mb-8">
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-white/60 mb-3">Premium</p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-serif text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>$14.99</span>
                <span className="text-white/60">/month</span>
              </div>
            </div>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-white">Unlimited Grace Assistant</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-white">Unlimited guests</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-white">Premium guest website</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-white">Unlimited timeline rebuilds</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-white">Full vendor directory access</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-[#881337] mt-0.5 flex-shrink-0" size={18} />
                <span className="text-white">Priority support</span>
              </li>
            </ul>
            <Button 
              data-testid="pricing-premium-btn"
              onClick={() => navigate('/signup?plan=premium')} 
              className="w-full py-4 bg-white hover:bg-white/90 text-[#2C2825] font-medium transition-all duration-300"
            >
              Get Premium
            </Button>
          </div>
        </div>
        
        <p className="text-center mt-10 text-[#5C5652]">
          Already planning?{' '}
          <button onClick={() => navigate('/wedding-insight')} className="text-[#881337] hover:underline">
            Get a one-time Wedding Insight for $99.
          </button>
        </p>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#881337] mb-4 text-center">Common Questions</p>
          <h2 
            className="text-3xl md:text-4xl font-serif text-center mb-14 text-[#2C2825]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Wedding Planning Questions Answered
          </h2>
          
          <div className="space-y-8">
            <div className="border-b border-[#E8E5E1] pb-8">
              <h3 className="text-lg font-medium text-[#2C2825] mb-3">How far in advance should I start planning my wedding?</h3>
              <p className="text-[#5C5652] leading-relaxed">
                Most couples start planning 12 to 18 months before their wedding date. This gives you time to book 
                popular vendors, venues, and secure your preferred dates. VowFlow generates a personalized timeline 
                based on your wedding date, so you&apos;ll know exactly when to tackle each task.
              </p>
            </div>
            
            <div className="border-b border-[#E8E5E1] pb-8">
              <h3 className="text-lg font-medium text-[#2C2825] mb-3">What should I do first when planning a wedding?</h3>
              <p className="text-[#5C5652] leading-relaxed">
                Start with the big decisions: set your budget, choose your wedding date, and book your venue. 
                These three decisions shape everything else. VowFlow helps you prioritize tasks so you focus 
                on what matters first instead of getting lost in details too early.
              </p>
            </div>
            
            <div className="border-b border-[#E8E5E1] pb-8">
              <h3 className="text-lg font-medium text-[#2C2825] mb-3">How do I create a wedding budget that works?</h3>
              <p className="text-[#5C5652] leading-relaxed">
                Start with a total number you&apos;re comfortable spending, then allocate percentages to each category: 
                venue (40-50%), catering (20-25%), photography (10-12%), and so on. VowFlow&apos;s budget planner tracks 
                your spending in real-time, so you can see exactly where your money is going.
              </p>
            </div>
            
            <div className="border-b border-[#E8E5E1] pb-8">
              <h3 className="text-lg font-medium text-[#2C2825] mb-3">How do I manage my wedding guest list?</h3>
              <p className="text-[#5C5652] leading-relaxed">
                Create categories (family, friends, work colleagues), set limits for each, and track RSVPs in one place. 
                VowFlow&apos;s guest list tool helps you organize contact information, meal preferences, and RSVP status 
                without juggling multiple spreadsheets.
              </p>
            </div>
            
            <div className="pb-8">
              <h3 className="text-lg font-medium text-[#2C2825] mb-3">What if my wedding plans change?</h3>
              <p className="text-[#5C5652] leading-relaxed">
                Plans change—that&apos;s normal. When you update your wedding date or circumstances shift, your timeline 
                should adapt. VowFlow automatically adjusts your task deadlines when your wedding date changes, 
                so you don&apos;t have to manually recalculate everything.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-20 border-t border-[#E8E5E1]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Heart className="text-[#881337]" size={20} fill="#881337" />
            <span className="text-lg font-serif text-[#2C2825]">VowFlow</span>
          </div>
          <p className="text-[#5C5652] text-sm">© 2025 VowFlow. Making wedding planning joyful.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
